import { assertAllowedRemoteMediaUrl } from "./remote-media-url";

const FETCH_TIMEOUT_MS = 3000;
const MAX_BYTES = 512 * 1024;
const MAX_REDIRECTS = 3;

function decodeHtmlEntities(value: string): string {
	return value
		.replace(/&amp;/g, "&")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
		.replace(/&apos;/g, "'")
		.replace(/&nbsp;/g, " ")
		.replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
		.replace(/&#x([0-9a-f]+);/gi, (_, hex) =>
			String.fromCharCode(Number.parseInt(hex, 16)),
		);
}

function extractMetaContent(
	html: string,
	attr: string,
	value: string,
): string | undefined {
	const metaTagRegex = new RegExp(
		`<meta[^>]*\\b${attr}\\s*=\\s*["']${value}["'][^>]*>`,
		"i",
	);
	const tag = html.match(metaTagRegex)?.[0];
	if (!tag) return undefined;
	const contentMatch = tag.match(/\bcontent\s*=\s*["']([^"']*)["']/i);
	const raw = contentMatch?.[1];
	if (!raw) return undefined;
	const decoded = decodeHtmlEntities(raw).trim();
	return decoded || undefined;
}

export function normalizeWebsiteUrl(website: string): string {
	const trimmed = website.trim();
	if (!trimmed) return "";
	if (/^https?:\/\//i.test(trimmed)) return trimmed;
	return `https://${trimmed}`;
}

export function isValidWebsiteInput(website: string): boolean {
	const normalized = normalizeWebsiteUrl(website);
	if (!normalized) return false;
	try {
		const url = new URL(normalized);
		return /^[^.\s]+(\.[^.\s]+)+$/.test(url.hostname);
	} catch {
		return false;
	}
}

async function fetchHtmlWithRedirects(
	sourceUrl: string,
	signal: AbortSignal,
	stopAtHead = true,
): Promise<string | null> {
	let currentUrl = assertAllowedRemoteMediaUrl(sourceUrl);

	for (
		let redirectCount = 0;
		redirectCount <= MAX_REDIRECTS;
		redirectCount += 1
	) {
		const response = await fetch(currentUrl, {
			redirect: "manual",
			signal,
			headers: {
				"User-Agent": "AgentMRR-Bot/1.0 (+https://agentmrr.com)",
				Accept: "text/html,application/xhtml+xml",
			},
		});

		if (response.status >= 300 && response.status < 400) {
			const location = response.headers.get("location");
			if (!location || redirectCount === MAX_REDIRECTS) return null;
			currentUrl = assertAllowedRemoteMediaUrl(
				new URL(location, currentUrl).toString(),
			);
			continue;
		}

		if (!response.ok) return null;

		const contentType = response.headers.get("content-type") ?? "";
		if (!contentType.toLowerCase().includes("html")) return null;

		const reader = response.body?.getReader();
		if (!reader) return await response.text();

		const decoder = new TextDecoder();
		let html = "";
		let received = 0;
		while (received < MAX_BYTES) {
			const { done, value } = await reader.read();
			if (done) break;
			received += value.byteLength;
			html += decoder.decode(value, { stream: true });
			if (stopAtHead && html.includes("</head>")) break;
		}
		html += decoder.decode();
		reader.cancel().catch(() => {});
		return html;
	}

	return null;
}

export async function fetchWebsiteDescription(
	website: string,
): Promise<string | null> {
	const normalized = normalizeWebsiteUrl(website);
	if (!normalized) return null;

	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

	try {
		const html = await fetchHtmlWithRedirects(normalized, controller.signal);
		if (!html) return null;

		const head = html.split(/<\/head>/i)[0] ?? html;

		const description =
			extractMetaContent(head, "name", "description") ??
			extractMetaContent(head, "property", "og:description") ??
			extractMetaContent(head, "name", "twitter:description");

		if (!description) return null;

		const truncated = description.slice(0, 600);
		return truncated.length >= 10 ? truncated : null;
	} catch {
		return null;
	} finally {
		clearTimeout(timeout);
	}
}

function extractReadableText(html: string): string {
	const withoutBlocks = html
		.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, " ")
		.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, " ")
		.replace(/<noscript\b[^>]*>[\s\S]*?<\/noscript>/gi, " ")
		.replace(/<!--[\s\S]*?-->/g, " ");
	const text = withoutBlocks.replace(/<[^>]+>/g, " ");
	return decodeHtmlEntities(text).replace(/\s+/g, " ").trim();
}

/**
 * Fetch a website's homepage and return its visible, tag-stripped text,
 * truncated to `maxChars`. Best-effort: returns null on fetch failure,
 * non-HTML responses, or too-little extractable text.
 */
export async function fetchWebsiteText(
	website: string,
	maxChars = 6000,
): Promise<string | null> {
	const normalized = normalizeWebsiteUrl(website);
	if (!normalized) return null;

	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

	try {
		const html = await fetchHtmlWithRedirects(
			normalized,
			controller.signal,
			false,
		);
		if (!html) return null;

		const text = extractReadableText(html);
		if (text.length < 20) return null;

		return text.slice(0, maxChars);
	} catch {
		return null;
	} finally {
		clearTimeout(timeout);
	}
}
